<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { ref, computed } from 'vue';
import { NxtLink, NxtIcon } from '@future-harry/nxt-styles';
import ApplyModal from '../components/modals/apply/ApplyModal.vue';
import PositionHeader from '../components/position/PositionHeader.vue';
import PositionBody from '../components/position/PositionBody.vue';
import ContactCard from '../components/position/ContactCard.vue';
import NxtButtonTheme from '../components/NxtButtonTheme.vue';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';
import ViewerDetailSkeleton from '@/apps/public/components/skeletons/pages/ViewerDetailSkeleton.vue';
import { storeToRefs } from 'pinia';
import { getPositionDetail } from '../../../core/collections/positions/positions.service';

const route = useRoute();
const router = useRouter();
const { currentJobBoard } = storeToRefs(useJobBoardStore());

const id = route.params.id as string;
const showModal = ref(false);
const applicationFields = computed(() => currentJobBoard.value?.application_fields ?? {});

const { data: position, isLoading } = getPositionDetail(id);

function openApplyModal() {
   showModal.value = true;
}

function closeApplyModal() {
   showModal.value = false;
}

function goHome() {
   router.push('/');
}
</script>

<template>
   <ViewerDetailSkeleton v-if="isLoading" class="position-detail--empty" />
   <div v-else-if="!isLoading && !position" class="position-detail--empty">
      <NxtLink @click="goHome">
         <NxtIcon name="arrow-left" color="var(--nxt-blue)" size="small" />
         <span class="ml-gutter">{{ $tb('public.position.go-back') }}</span>
      </NxtLink>
      <h2>{{ $tb('public.position.not-found') }}</h2>
   </div>
   <div v-else-if="!isLoading && position">
      <PositionHeader :position="position" />
      <div class="position-detail-wrapper">
         <NxtLink @click="goHome">
            <NxtIcon name="arrow-left" color="var(--nxt-blue)" size="small" />
            <span class="ml-gutter">{{ $tb('public.position.go-back') }}</span>
         </NxtLink>
         <div class="position-detail">
            <div>
               <PositionBody :position="position" />
            </div>
            <div>
               <NxtButtonTheme @click="openApplyModal">{{ $tb('public.apply.button') }}</NxtButtonTheme>
               <ContactCard v-if="position && position.user_spoc" :contact="position.user_spoc" />
            </div>
         </div>
      </div>
      <ApplyModal v-if="showModal" :applicationFields="applicationFields" :position-id="id" @on-close="closeApplyModal" />
   </div>
</template>

<style scoped lang="scss">
.position-detail-wrapper {
   max-width: 1000px;
   margin: 0 auto;
   padding: var(--nxt-gutter-xlarge);
}

.position-detail {
   display: grid;
   grid-template-columns: 70% 30%;
   gap: var(--nxt-gutter-xlarge);
   box-sizing: border-box;
   margin-top: var(--nxt-gutter);

   &--empty {
      max-width: 1000px;
      padding: var(--nxt-gutter-large);
      margin: 0 auto;
   }
}

:deep(.nxt-link) {
   color: var(--nxt-blue);
}

.ml-gutter {
   margin-left: var(--nxt-gutter);
}

@media (max-width: 768px) {
   .position-detail {
      grid-template-columns: 1fr;
   }
}
</style>
